// frontend/src/components/OrgSwitcher.jsx
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";

/** Tailwind classes for each role's badge colour. */
const ROLE_BADGE = {
  owner: "bg-purple-100 text-purple-700",
  admin: "bg-blue-100 text-blue-700",
  member: "bg-gray-100 text-gray-600",
};

/** Small coloured pill showing a member's role. */
function RoleBadge({ role }) {
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
        ROLE_BADGE[role] || ROLE_BADGE.member
      }`}
    >
      {role}
    </span>
  );
}
RoleBadge.propTypes = { role: PropTypes.string.isRequired };

/**
 * OrgSwitcher — dropdown showing the active org + role, listing all of the
 * user's orgs. Selecting one calls `switchOrg`, which re-scopes the session.
 *
 * Renders nothing until the active org is known.
 */
function OrgSwitcher() {
  const { currentOrg, orgs, switchOrg } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  // Close the menu on an outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!currentOrg) return null;

  const handleSelect = async (orgId) => {
    setOpen(false);
    if (orgId === currentOrg.id || busy) return;
    setBusy(true);
    try {
      await switchOrg(orgId);
    } catch {
      toast.error("Could not switch organization. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 max-w-[220px] px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-sm hover:bg-gray-50 transition-colors disabled:opacity-60"
      >
        <span className="truncate font-medium text-gray-800">
          {currentOrg.name}
        </span>
        <RoleBadge role={currentOrg.role} />
        <svg
          className="w-4 h-4 text-gray-400 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <ul
          role="menu"
          className="absolute left-0 mt-1 w-64 max-h-72 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg py-1 z-50"
        >
          {orgs.map((org) => {
            const active = org.org_id === currentOrg.id;
            return (
              <li key={org.org_id}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleSelect(org.org_id)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors ${
                    active ? "bg-blue-50" : "hover:bg-gray-50"
                  }`}
                >
                  <span className="truncate text-gray-800">{org.name}</span>
                  <RoleBadge role={org.role} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default OrgSwitcher;
